import React from 'react';
import { Box, Tabs, Tab, IconButton, Tooltip, TextField, InputAdornment, useTheme, useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setInitialTrackIndexAction } from '../../../store/actions/watchlist';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import ColumnFilterPopover from './ColumnFilterPopover';
import SearchBar from '../SearchBar';

const TouchableTab = React.memo(({ label, value, selected, onChange }) => {
  const theme = useTheme();
  return (
    <Tab
      label={label}
      value={value}
      onClick={(e) => onChange(e, value)}
      sx={{
        height: '48px',
        minHeight: '48px',
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: selected ? 600 : 400,
        opacity: selected ? 1 : 0.7,
        color: selected ? theme.palette.primary.main : 'text.primary',
      }}
    />
  );
});

const ExpandedViewHeader = ({ watchlists, watchListIndex, addInstrument, columnSettings }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleOpenFilter = (event) => setAnchorEl(event.currentTarget);
  const handleCloseFilter = () => setAnchorEl(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        px: 2,
        py: isMobile ? 1 : 0,
        height: isMobile ? 'auto' : '64px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: isMobile ? 1 : 2,
        position: 'relative',
        zIndex: 1100,
      }}
    >
      {/* Top row / Left side: Watchlist Tabs */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        overflow: 'hidden',
        width: isMobile ? '100%' : 'auto' 
      }}>
        <Tabs
          value={watchListIndex || 0}
          onChange={(_event, selectedId) => {
            dispatch(setInitialTrackIndexAction());
            dispatch({
              type: "SET_WATCHLISTINDEX",
              payload: selectedId,
            });
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: '48px',
            width: '100%',
            "& .MuiTabs-indicator": {
              backgroundColor: theme.palette.primary.main,
              height: 3,
            },
          }}
        >
          {watchlists?.map((mw, index) => (
            <TouchableTab
              key={mw.id || index}
              label={mw.mappedName || mw.name || `Watchlist ${index + 1}`}
              value={index}
              selected={watchListIndex === index}
              onChange={(_event, selectedId) => {
                dispatch(setInitialTrackIndexAction());
                dispatch({
                  type: "SET_WATCHLISTINDEX",
                  payload: selectedId,
                });
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Bottom row / Right side: Search & Actions */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        justifyContent: isMobile ? 'space-between' : 'flex-end'
      }}>
        {/* Production Search Bar */}
        <Box sx={{ width: isMobile ? '100%' : '300px' }}>
          <SearchBar
            marketWatches={watchlists}
            activeTab={watchListIndex}
            currentWatchlist={watchlists[watchListIndex]}
            addInstrument={addInstrument}
            setHighlightedInstrument={() => {}} // Placeholder for highlight logic
            isExpandedView={true}
          />
        </Box>

        {/* Column Filter Button */}
        <Tooltip title="Manage Columns">
          <IconButton 
            size="small" 
            onClick={handleOpenFilter}
            sx={{
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: 'action.selected' },
              flexShrink: 0
            }}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <ColumnFilterPopover
          anchorEl={anchorEl}
          onClose={handleCloseFilter}
          {...columnSettings}
        />

        {/* Collapse Button */}
        <Tooltip title="Collapse Watchlist">
          <IconButton 
            size="small" 
            onClick={() => navigate('/dashboard')}
            sx={{
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: 'action.selected' },
              flexShrink: 0
            }}
          >
            <CloseFullscreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ExpandedViewHeader;
